import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Heart, MessageCircle, Share2, Copy, Star, Repeat2, Play, Loader2,
  ChevronDown, ChevronUp, Send, X, DollarSign, User as UserIcon,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import VideoPlayer from "@/components/VideoPlayer";
import ShareButton from "@/components/ShareButton";

/**
 * Reels — vertical swipe feed backed by /api/videos (Slice c).
 *
 * Actions (viewer):
 *   Rate     → POST /api/ratings          (target_type=video)
 *   Comment  → POST /api/videos/{id}/comment  (reuses feed_comments)
 *   Save     → POST /api/favorites/toggle (target_type=video · Slice a)
 *   Share    → native + copy fallback (existing ShareButton)
 *   Remix    → opens remix composer → POST /api/videos/{id}/remix
 *   Tip      → POST /api/videos/{id}/tip  (wallet_ledger two-legged)
 *   Chat     → POST /api/conversations/start with creator (no phone exposure)
 *
 * Creator actions live on /video-intros (upload) — Reels is the viewer feed.
 */
const CREATOR_TYPES = [
  { k: "",           l: "All"          },
  { k: "vlogger",    l: "Vloggers"     },
  { k: "educator",   l: "Educators"    },
  { k: "youtuber",   l: "YouTubers"    },
  { k: "influencer", l: "Influencers"  },
  { k: "celebrity",  l: "Celebrities"  },
  { k: "sponsored",  l: "Sponsored"    },
  { k: "business",   l: "Business"     },
];

export default function Reels() {
  const { user } = useAuth();
  const [videos, setVideos] = useState(null);
  const [idx, setIdx] = useState(0);
  const [filter, setFilter] = useState("");
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [remixOpen, setRemixOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const params = { limit: 30 };
    if (filter) params.creator_type = filter;
    api.get("/videos", { params })
      .then(r => setVideos(r.data || []))
      .catch(() => setVideos([]));
    setIdx(0);
  }, [filter]);

  const cur = videos?.[idx];

  const next = () => setIdx(i => Math.min((videos?.length || 1) - 1, i + 1));
  const prev = () => setIdx(i => Math.max(0, i - 1));
  // Wheel + keyboard nav — desktop; mobile users swipe the container.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    let last = 0;
    const onWheel = (e) => {
      const now = Date.now();
      if (now - last < 500) return;
      last = now;
      if (e.deltaY > 30) next();
      else if (e.deltaY < -30) prev();
    };
    const onKey = (e) => {
      if (e.key === "ArrowDown" || e.key === "j") next();
      if (e.key === "ArrowUp"   || e.key === "k") prev();
    };
    el.addEventListener("wheel", onWheel, { passive: true });
    window.addEventListener("keydown", onKey);
    return () => { el.removeEventListener("wheel", onWheel); window.removeEventListener("keydown", onKey); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videos]);

  return (
    <div ref={containerRef} className="min-h-screen bg-black text-white flex" data-testid="reels-page">
      {/* Vertical rail — creator type filter */}
      <div className="hidden sm:flex flex-col gap-2 p-4 pt-6 w-40 border-r border-white/10">
        <div className="text-[10px] uppercase tracking-widest text-white/60 font-bold">Reels</div>
        {CREATOR_TYPES.map(({ k, l }) => (
          <button
            key={k || "all"}
            onClick={() => setFilter(k)}
            className={`text-left text-xs font-bold px-3 py-2 rounded-full ${
              filter === k ? "bg-white text-black" : "bg-white/5 hover:bg-white/10"
            }`}
            data-testid={`reels-filter-${k || "all"}`}
          >
            {l}
          </button>
        ))}
      </div>

      {/* Main feed */}
      <div className="flex-1 relative flex items-center justify-center">
        {videos === null && <Loader2 className="animate-spin text-white/50" size={32} />}
        {videos !== null && videos.length === 0 && (
          <div className="text-center text-white/60" data-testid="reels-empty">
            <Play size={40} className="mx-auto opacity-40" />
            <div className="font-bold mt-2">No reels yet</div>
            <div className="text-xs mt-1">Upload one via <Link to="/video-intros" className="underline">Video Intros</Link>.</div>
          </div>
        )}
        {cur && (
          <div className="relative w-full max-w-md mx-auto py-6" data-testid={`reel-${cur.video_id}`}>
            <div className="aspect-[9/16] bg-stone-900 rounded-2xl overflow-hidden border border-white/10">
              <VideoPlayer url={cur.url} className="!rounded-none" testid="reel-video" autoPlay />
            </div>

            {/* Meta */}
            <div className="mt-3 flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-xs text-white/60 flex items-center gap-1.5">
                  <UserIcon size={11} /> {cur.creator_name || "Unknown creator"}
                  {cur.creator_type && (
                    <span className="ml-1 px-1.5 py-0.5 rounded bg-white/10 text-[10px] uppercase tracking-wider">
                      {cur.creator_type}
                    </span>
                  )}
                  {cur.remix_of && (
                    <span className="ml-1 px-1.5 py-0.5 rounded bg-fuchsia-500/20 text-[10px] uppercase">
                      remix
                    </span>
                  )}
                </div>
                <h2 className="font-black mt-1 truncate" data-testid="reel-title">{cur.title}</h2>
                {cur.description && (
                  <p className="text-sm text-white/70 mt-1 line-clamp-2">{cur.description}</p>
                )}
              </div>
            </div>

            {/* Action rail (viewer) */}
            <ActionRail
              video={cur}
              user={user}
              onCommentsOpen={() => setCommentsOpen(true)}
              onRemixOpen={() => setRemixOpen(true)}
            />

            {/* Nav */}
            <div className="mt-4 flex items-center justify-center gap-2">
              <button onClick={prev} disabled={idx === 0}
                      className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 grid place-items-center"
                      data-testid="reels-prev">
                <ChevronUp size={16} />
              </button>
              <span className="text-xs text-white/60">{idx + 1} / {videos.length}</span>
              <button onClick={next} disabled={idx >= videos.length - 1}
                      className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 grid place-items-center"
                      data-testid="reels-next">
                <ChevronDown size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {commentsOpen && cur && (
        <CommentsSheet video={cur} onClose={() => setCommentsOpen(false)} user={user} />
      )}
      {remixOpen && cur && (
        <RemixSheet video={cur} onClose={() => setRemixOpen(false)} onDone={() => setRemixOpen(false)} />
      )}
    </div>
  );
}


/* ----------------------- action rail ----------------------- */
function ActionRail({ video, user, onCommentsOpen, onRemixOpen }) {
  const [rating, setRating] = useState(0);
  const [ratingBusy, setRatingBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [likeBusy, setLikeBusy] = useState(false);
  const [tipOpen, setTipOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/favorites/check", { params: { target_type: "video", target_id: video.video_id } })
       .then(r => setSaved(!!r.data?.favorited)).catch(() => {});
    api.get(`/ratings/video/${video.video_id}`)
       .then(r => setRating(r.data?.mine || 0)).catch(() => {});
  }, [user, video.video_id]);

  const toggleSave = async () => {
    if (!user) { toast.error("Sign in to save"); return; }
    setLikeBusy(true);
    try {
      const r = await api.post("/favorites/toggle",
                                { target_type: "video", target_id: video.video_id });
      setSaved(!!r.data?.favorited);
      toast.success(r.data?.favorited ? "Saved to your Saved page" : "Removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setLikeBusy(false); }
  };

  const rate = async (n) => {
    if (!user) { toast.error("Sign in to rate"); return; }
    if (ratingBusy) return;
    setRatingBusy(true);
    try {
      await api.post("/ratings", {
        target_type: "video", target_id: video.video_id, rating: n,
      });
      setRating(n); toast.success(`Rated ${n}★`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rating failed");
    } finally { setRatingBusy(false); }
  };

  const startChat = async () => {
    if (!user) { toast.error("Sign in to chat"); return; }
    try {
      const r = await api.post("/conversations/start",
                                { user_id: video.creator_user_id });
      window.location.href = `/messages/${r.data.conv_id}`;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start chat");
    }
  };

  const shareUrl = `${window.location.origin}/reels?v=${video.video_id}`;

  return (
    <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="reel-actions">
      {/* Rate 1-5 (compact star row) */}
      <div className="flex items-center gap-0.5" data-testid="reel-rate">
        {[1,2,3,4,5].map(n => (
          <button key={n} onClick={() => rate(n)} disabled={ratingBusy}
                  className="p-1 rounded hover:bg-white/10"
                  data-testid={`reel-star-${n}`}
                  aria-label={`Rate ${n} stars`}>
            <Star size={14} className={n <= rating ? "fill-yellow-400 text-yellow-400" : "text-white/40"} />
          </button>
        ))}
      </div>

      <button onClick={onCommentsOpen}
              className="inline-flex items-center gap-1 rounded-full bg-white/10 hover:bg-white/20 px-3 py-1.5 text-xs font-bold"
              data-testid="reel-comment-btn">
        <MessageCircle size={13} /> {video.comment_count || 0}
      </button>

      <button onClick={toggleSave} disabled={likeBusy}
              className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-bold ${
                saved ? "bg-rose-500 hover:bg-rose-600" : "bg-white/10 hover:bg-white/20"
              }`}
              data-testid="reel-save-btn"
              data-saved={saved ? "true" : "false"}>
        <Heart size={13} fill={saved ? "currentColor" : "none"} /> Save
      </button>

      <ShareButton
        url={shareUrl}
        title={video.title}
        className="!bg-white/10 hover:!bg-white/20 !text-white text-xs px-3 py-1.5"
        data-testid="reel-share-btn"
      >
        <Share2 size={13} /> Share
      </ShareButton>

      <button onClick={onRemixOpen}
              className="inline-flex items-center gap-1 rounded-full bg-white/10 hover:bg-white/20 px-3 py-1.5 text-xs font-bold"
              data-testid="reel-remix-btn">
        <Repeat2 size={13} /> Remix
      </button>

      {user && video.creator_user_id !== user.user_id && (
        <button onClick={startChat}
                className="inline-flex items-center gap-1 rounded-full bg-white/10 hover:bg-white/20 px-3 py-1.5 text-xs font-bold"
                data-testid="reel-chat-btn"
                title="Message the creator">
          <Send size={13} /> Chat
        </button>
      )}

      {user && video.creator_user_id !== user.user_id && (
        <button disabled title="Tips are paused until real wallet payments are enabled"
                className="inline-flex items-center gap-1 rounded-full bg-[#E05E36]/50 cursor-not-allowed px-3 py-1.5 text-xs font-bold"
                data-testid="reel-tip-btn">
          <DollarSign size={13} /> Tips soon
        </button>
      )}

      {tipOpen && (
        <TipSheet video={video} onClose={() => setTipOpen(false)} />
      )}
    </div>
  );
}


/* ------------------------- comments sheet ------------------------- */
function CommentsSheet({ video, onClose, user }) {
  const [rows, setRows] = useState(null);
  const [txt, setTxt] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/videos/${video.video_id}/comments`)
       .then(r => setRows(r.data || []));
  }, [video.video_id]);

  const post = async () => {
    if (!user) { toast.error("Sign in to comment"); return; }
    const body = txt.trim();
    if (!body) return;
    setBusy(true);
    try {
      const r = await api.post(`/videos/${video.video_id}/comment`, { body });
      setRows([r.data, ...(rows || [])]);
      setTxt("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not post");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 grid place-items-center p-4 z-50" style={{ zIndex: "var(--z-modal)" }}
         onClick={onClose} data-testid="reel-comments-sheet">
      <div className="bg-white text-stone-900 rounded-2xl w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col"
           onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-stone-100 flex items-center justify-between">
          <h3 className="font-black">Comments</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-stone-100" aria-label="Close">
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-3">
          {rows === null && <Loader2 className="animate-spin mx-auto text-stone-400" />}
          {rows?.length === 0 && (
            <div className="text-center text-xs text-stone-500 py-8">Be the first to comment.</div>
          )}
          {rows?.map(c => (
            <div key={c.comment_id} className="flex gap-2" data-testid={`reel-comment-${c.comment_id}`}>
              <div className="w-8 h-8 rounded-full bg-stone-200 shrink-0 grid place-items-center text-xs font-bold text-stone-500">
                {(c.user_name || "?").charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-bold">{c.user_name || "Anon"}</div>
                <div className="text-sm text-stone-800 break-words">{c.body}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="p-3 border-t border-stone-100 flex gap-2">
          <input value={txt} onChange={e => setTxt(e.target.value)}
                 onKeyDown={e => e.key === "Enter" && post()}
                 placeholder="Add a comment…"
                 className="flex-1 border border-stone-300 rounded-full px-3 py-2 text-sm"
                 data-testid="reel-comment-input" />
          <button onClick={post} disabled={busy || !txt.trim()}
                  className="rounded-full bg-[#2D5A27] hover:bg-[#24491f] disabled:opacity-50 text-white text-xs font-bold px-4"
                  data-testid="reel-comment-post">
            {busy ? <Loader2 size={12} className="animate-spin" /> : "Post"}
          </button>
        </div>
      </div>
    </div>
  );
}


/* ------------------------- remix composer ------------------------- */
function RemixSheet({ video, onClose, onDone }) {
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState(`Remix of ${video.title}`);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!url.trim() || !title.trim()) { toast.error("URL and title required"); return; }
    setBusy(true);
    try {
      await api.post(`/videos/${video.video_id}/remix`, {
        url: url.trim(), title: title.trim(),
        creator_type: "vlogger",
      });
      toast.success("Remix published");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remix failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 grid place-items-center p-4 z-50" style={{ zIndex: "var(--z-modal)" }}
         onClick={onClose} data-testid="reel-remix-sheet">
      <div className="bg-white text-stone-900 rounded-2xl w-full max-w-md p-4"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-black">Remix this reel</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-stone-100" aria-label="Close">
            <X size={16} />
          </button>
        </div>
        <label className="block text-xs font-bold mb-1">Your remix video URL (YouTube / Vimeo / mp4)</label>
        <input value={url} onChange={e => setUrl(e.target.value)}
               className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm"
               placeholder="https://…"
               data-testid="remix-url" />
        <label className="block text-xs font-bold mt-3 mb-1">Title</label>
        <input value={title} onChange={e => setTitle(e.target.value)}
               className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm"
               data-testid="remix-title" />
        <button onClick={submit} disabled={busy}
                className="mt-4 w-full rounded-full bg-[#E05E36] hover:bg-[#c94e2a] disabled:opacity-50 text-white font-bold py-2 text-sm"
                data-testid="remix-submit">
          {busy ? "Publishing…" : "Publish remix"}
        </button>
      </div>
    </div>
  );
}


/* ------------------------- tip sheet ------------------------- */
function TipSheet({ video, onClose }) {
  const [amount, setAmount] = useState(10000);   // 100 rupees
  const [busy, setBusy] = useState(false);

  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/videos/${video.video_id}/tip`,
                                { amount_paise: amount });
      toast.success(`Tipped ₹${(r.data.amount_paise/100).toFixed(0)} to ${r.data.creator_name}`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Tip failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 grid place-items-center p-4 z-50" style={{ zIndex: "var(--z-modal)" }}
         onClick={onClose} data-testid="reel-tip-sheet">
      <div className="bg-white text-stone-900 rounded-2xl w-full max-w-sm p-4"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-black">Tip creator</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-stone-100" aria-label="Close">
            <X size={16} />
          </button>
        </div>
        <div className="grid grid-cols-4 gap-2 mb-3">
          {[5, 10, 50, 100].map(rs => (
            <button key={rs} onClick={() => setAmount(rs * 100)}
                    className={`rounded-full py-2 text-xs font-bold ${
                      amount === rs * 100 ? "bg-[#2D5A27] text-white" : "bg-stone-100"
                    }`}
                    data-testid={`tip-amt-${rs}`}>
              ₹{rs}
            </button>
          ))}
        </div>
        <button onClick={send} disabled={busy}
                className="w-full rounded-full bg-[#E05E36] hover:bg-[#c94e2a] disabled:opacity-50 text-white font-bold py-2 text-sm"
                data-testid="tip-send">
          {busy ? "Sending…" : `Tip ₹${(amount / 100).toFixed(0)}`}
        </button>
      </div>
    </div>
  );
}
